// kasir.repo — data Tutup Kasir Harian. Mengambil tiket yang MENYENTUH KAS pada tanggal tertentu.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';
import type { KasTiket } from '@domain/kasir';

const SEL = 'no,outlet,unit,biaya,biaya_simpan,dp,lunas,verified,cara_bayar,dp_via,lunas_via,tgl_iso,tgl_ambil_iso,lunas_at,arsip_keu,refund,status,customers(nama)';

interface Raw {
  no: string; outlet: string | null; unit: string | null; biaya: number | null; biaya_simpan: number | null;
  dp: number | null; lunas: boolean | null; verified: boolean | null; cara_bayar: string | null;
  dp_via: string | null; lunas_via: string | null; tgl_iso: string | null; tgl_ambil_iso: string | null;
  lunas_at: string | null; arsip_keu: boolean | null; status: string;
  refund: { amount?: number; status?: string; verifiedAt?: string; method?: string } | null;
  customers: { nama: string } | { nama: string }[] | null;
}

const toKas = (r: Raw): KasTiket & { status: string; lunas: boolean; verified: boolean } => {
  const c = Array.isArray(r.customers) ? r.customers[0] : r.customers;
  const ref = r.refund;
  return {
    no: r.no, outlet: r.outlet, custNama: c?.nama ?? '—', unit: r.unit ?? '—',
    biaya: r.biaya ?? 0, biayaSimpan: r.biaya_simpan ?? 0, dp: r.dp ?? 0,
    lunas: !!r.lunas, verified: !!r.verified, caraBayar: r.cara_bayar,
    dpVia: r.dp_via, lunasVia: r.lunas_via,
    tglISO: r.tgl_iso, tglAmbilISO: r.tgl_ambil_iso, lunasAt: r.lunas_at,
    refundAmount: ref?.status === 'verified' ? (ref.amount ?? 0) : 0,
    refundAt: ref?.status === 'verified' ? (ref.verifiedAt ?? null) : null,
    refundVia: ref?.status === 'verified' ? (ref.method ?? null) : null,
    arsipKeu: !!r.arsip_keu, status: r.status,
  };
};

/**
 * Tiket yang berpotensi menyentuh kas pada `tgl` (YYYY-MM-DD lokal).
 * Empat pintu masuk uang → empat kondisi OR, disaring lagi tepat di `domain/kasir`:
 *   tgl_iso (DP) · tgl_ambil_iso (sisa) · lunas_at (pelunasan tempo) · refund terverifikasi.
 * `lunas_at` & refund bertipe timestamptz/jsonb → disaring rentang sehari penuh, bukan sama-dengan.
 * RLS tetap membatasi cabang; parameter `outlet` hanya mempersempit untuk Owner/Manager.
 */
export async function ticketsForDate(tgl: string, outlet?: string | null): Promise<(KasTiket & { status: string })[]> {
  const sb = supabase();
  // Offset toko EKSPLISIT: tanpa ini rentang ditafsirkan pada TZ sesi Postgres (Supabase default UTC),
  // sehingga pelunasan dini hari WIB tercecer ke hari kemarin. Harus sepadan dgn TZ_TOKO di domain.
  const awal = `${tgl}T00:00:00+07:00`, akhir = `${tgl}T23:59:59.999+07:00`;
  const base = () => sb.from('tickets').select(SEL).eq('arsip_keu', false);

  const [a, b, c] = await Promise.all([
    base().eq('tgl_iso', tgl),                                  // DP masuk saat terima servis
    base().eq('tgl_ambil_iso', tgl),                            // sisa saat unit keluar
    base().gte('lunas_at', awal).lte('lunas_at', akhir),        // pelunasan tempo hari itu
  ]);
  for (const r of [a, b, c]) if (r.error) throw new Error(r.error.message);

  // Refund: tanggalnya di dalam jsonb → saring di SERVER lewat operator ->> (jangan tarik seluruh
  // riwayat refund tiap render; selain berat, baris tak relevan bisa mencemari perhitungan).
  const { data: rf, error: rfErr } = await sb.from('tickets').select(SEL).eq('arsip_keu', false)
    .gte('refund->>verifiedAt', awal).lte('refund->>verifiedAt', akhir);
  if (rfErr) throw new Error(rfErr.message);

  const byNo = new Map<string, Raw>();
  for (const row of [...(a.data ?? []), ...(b.data ?? []), ...(c.data ?? []), ...(rf ?? [])] as unknown as Raw[]) byNo.set(row.no, row);
  const rows = [...byNo.values()].map(toKas);
  const semua = [...rows, ...(await sesiLamaForDate(tgl))];
  return outlet ? semua.filter((r) => (r.outlet ?? '') === outlet) : semua;
}

interface RoundRaw {
  ticket_no: string; ronde: number; biaya: number | null; biaya_simpan: number | null; dp: number | null;
  lunas: boolean | null; cara_bayar: string | null; refund: { amount?: number; status?: string; verifiedAt?: string; method?: string } | null;
  data: { tgl_ambil_iso?: string | null; tgl_iso?: string | null; lunas_at?: string | null; lunas_via?: string | null; dp_via?: string | null } | null;
  tickets: { outlet: string | null; unit: string | null; arsip_keu: boolean | null; customers: { nama: string } | { nama: string }[] | null } | null;
}

/**
 * Sesi SERVIS-ULANG yang sudah beku (`ticket_rounds`) — tiap sesi punya hari & uangnya sendiri.
 *
 * Kenapa perlu: saat klaim garansi, kolom uang di `tickets` DIRESET (biaya=0, lunas=false,
 * tgl_ambil_iso=null) dan nilai lamanya pindah ke `ticket_rounds`. Tanpa membaca tabel itu,
 * uang yang SUDAH diterima di sesi sebelumnya lenyap dari Tutup Kasir — bug nyata yang ditemukan
 * Apo: pembayaran Rp 375.000 tanggal 19 Juli tampil Rp 0.
 *
 * Tiap sesi dijadikan satu "tiket semu" ber-nomor `NO (sesi N)` supaya `domain/kasir` bisa
 * menghitungnya seperti biasa tanpa perlu tahu soal sesi.
 */
async function sesiLamaForDate(tgl: string): Promise<(KasTiket & { status: string })[]> {
  // Sesi servis-ulang bisa menumpuk lintas tahun → WAJIB paging, kalau tidak kas sesi lama
  // hilang dari Tutup Kasir (selisih palsu). Tanggal sesi ada di jsonb `data` → saring di klien.
  const data = await fetchAllPaged<RoundRaw>((a, b) => supabase().from('ticket_rounds')
    .select('ticket_no,ronde,biaya,biaya_simpan,dp,lunas,cara_bayar,refund,data,tickets(outlet,unit,arsip_keu,customers(nama))')
    .order('id').range(a, b) as unknown as PromiseLike<{ data: RoundRaw[] | null; error: { message: string } | null }>);
  const out: (KasTiket & { status: string })[] = [];
  for (const r of data) {
    const t = Array.isArray(r.tickets) ? r.tickets[0] : r.tickets;
    if (!t || t.arsip_keu) continue;                       // finScope: tutup buku dikecualikan
    const d = r.data ?? {};
    // Saring di klien: tanggal sesi ada di dalam jsonb `data`, dan jumlahnya kecil (hanya servis-ulang).
    const sentuh = [d.tgl_ambil_iso, d.tgl_iso].some((x) => (x ?? '').slice(0, 10) === tgl)
      || (d.lunas_at ?? '').slice(0, 10) === tgl
      || (r.refund?.status === 'verified' && (r.refund.verifiedAt ?? '').slice(0, 10) === tgl);
    if (!sentuh) continue;
    const c = Array.isArray(t.customers) ? t.customers[0] : t.customers;
    const ref = r.refund;
    out.push({
      no: `${r.ticket_no} (sesi ${r.ronde})`, outlet: t.outlet, custNama: c?.nama ?? '—', unit: t.unit ?? '—',
      biaya: r.biaya ?? 0, biayaSimpan: r.biaya_simpan ?? 0, dp: r.dp ?? 0, lunas: !!r.lunas,
      caraBayar: r.cara_bayar, dpVia: d.dp_via ?? null, lunasVia: d.lunas_via ?? null,
      tglISO: d.tgl_iso ?? null, tglAmbilISO: d.tgl_ambil_iso ?? null, lunasAt: d.lunas_at ?? null,
      refundAmount: ref?.status === 'verified' ? (ref.amount ?? 0) : 0,
      refundAt: ref?.status === 'verified' ? (ref.verifiedAt ?? null) : null,
      refundVia: ref?.status === 'verified' ? (ref.method ?? null) : null,
      arsipKeu: false, status: 'Sudah Diambil',
    });
  }
  return out;
}

/** Pelunasan lunas TANPA jejak tanggal (data lama) — ditampilkan terpisah, tak ditebak masuk hari mana pun. */
export async function tempoTanpaTanggal(outlet?: string | null): Promise<(KasTiket & { status: string })[]> {
  const { data, error } = await supabase().from('tickets').select(SEL)
    .eq('arsip_keu', false).eq('lunas', true).is('lunas_at', null)
    // Dua jenis hutang: "Tempo (Toko)" & "Piutang" — pola literal (bukan input user), aman di .or().
    .or('cara_bayar.ilike.%tempo%,cara_bayar.ilike.%piutang%');
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown as Raw[]).map(toKas);
  return outlet ? rows.filter((r) => (r.outlet ?? '') === outlet) : rows;
}

// ---------- Penutupan harian ----------
export interface SetoranRow { jenis: 'bank' | 'tunai'; tujuan?: string; jumlah: number; ref?: string;
  /** Akun asal/tujuan + id mutasi kas. Tanpa ini setoran hanya jadi catatan: saldo akun tak
   *  bergerak dan "Setoran hari ini" di analisa selisih selamanya Rp 0. */
  dariId?: number; keId?: number; moveId?: number }
export interface DailyClose {
  id?: number; tgl: string; outlet: string | null;
  expect: Record<string, number>; actual: Record<string, number>; setoran: SetoranRow[];
  catatan: string | null;
  closed_by: string | null; closed_at: string | null;
  verified_by: string | null; verified_at: string | null;
}

export async function getClose(tgl: string, outlet: string | null): Promise<DailyClose | null> {
  let q = supabase().from('daily_close').select('*').eq('tgl', tgl);
  q = outlet ? q.eq('outlet', outlet) : q.is('outlet', null);
  const { data, error } = await q.maybeSingle();
  if (error) throw new Error(error.message);
  return (data as DailyClose | null) ?? null;
}

/**
 * Riwayat penutupan. Tanpa rentang → 60 hari terakhir; dengan rentang → dipakai laporan bulanan.
 * RLS tetap membatasi cabang; parameter `outlet` hanya mempersempit untuk Owner/Manager.
 */
export async function listCloses(opts: { dari?: string; sampai?: string; outlet?: string | null; limit?: number } = {}): Promise<DailyClose[]> {
  let q = supabase().from('daily_close').select('*').order('tgl', { ascending: false });
  if (opts.dari) q = q.gte('tgl', opts.dari);
  if (opts.sampai) q = q.lte('tgl', opts.sampai);
  if (opts.outlet) q = q.eq('outlet', opts.outlet);
  if (!opts.dari && !opts.sampai) q = q.limit(opts.limit ?? 60);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as DailyClose[];
}

/** Simpan penutupan (upsert per tanggal+cabang). `expect` dibekukan di sini = snapshot saat ditutup. */
/**
 * `tutup=false` → hanya menyimpan (mis. mencatat setoran di tengah hari). Jangan menstempel
 * "ditutup": kalau distempel, Finance melihat hari itu siap diverifikasi padahal hitungan fisik
 * belum disentuh.
 * `tutup=true` → stempel penutup, DAN cabut verifikasi lama bila angkanya diubah setelah
 * diverifikasi — supaya stempel "✓ diverifikasi oleh X" tak menggantung di angka yang X tak pernah lihat.
 */
export async function saveClose(d: Omit<DailyClose, 'id' | 'verified_by' | 'verified_at'>, tutup: boolean): Promise<void> {
  const stamp = tutup
    ? { closed_by: currentUser()?.nama ?? '', closed_at: new Date().toISOString(), verified_by: null, verified_at: null }
    : {};
  const { error } = await supabase().from('daily_close')
    .upsert({ ...d, ...stamp }, { onConflict: 'tgl,outlet' });
  if (error) throw new Error(error.message);
}

export async function verifyClose(tgl: string, outlet: string | null): Promise<void> {
  let q = supabase().from('daily_close')
    .update({ verified_by: currentUser()?.nama ?? '', verified_at: new Date().toISOString() })
    .eq('tgl', tgl);
  q = outlet ? q.eq('outlet', outlet) : q.is('outlet', null);
  // .select() wajib: update yang mengenai 0 baris (mis. ditolak RLS/trigger) TIDAK melempar error —
  // tanpa ini toast bilang "terverifikasi" padahal tak ada yang berubah.
  const { data, error } = await q.select('id');
  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error('Verifikasi ditolak — periksa izin Anda, atau penutupan belum ada.');
}
